"use client"
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselNavigationArrows,
  CarouselNavigationArrowsNext,
  CarouselNavigationArrowsPrevious,
  classNames,
  Flex,
  FlexColumn,
  Media,
  SpaceBetween,
  TextBody,
  TextHeading,
} from "@opensea/ui-kit"
import { useIsomorphicLayoutEffect } from "@opensea/ui-kit/hooks"
import { createContext, useRef, useState } from "react"
import { PreviewActionBar } from "@/components/studio/page-editor/PreviewActionBar"

type OverviewModuleCarouselProps = {
  title?: string | null
  description?: string | null
  children: React.ReactNode
  moduleIndex?: number
}

type OverviewModuleCarouselContextValue = {
  contentHeight?: number
}

export const OverviewModuleCarouselContext =
  createContext<OverviewModuleCarouselContextValue>({
    contentHeight: undefined,
  })

export function OverviewModuleCarousel({
  title,
  description,
  children,
  moduleIndex,
}: OverviewModuleCarouselProps) {
  const [api, setApi] = useState<CarouselApi | null>(null)
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const canScroll = api?.canScrollNext() || api?.canScrollPrev()

  useIsomorphicLayoutEffect(() => {
    const height = containerRef.current?.clientHeight
    if (height) {
      // Add 1px to height to ensure textoverflow is applied correctly
      setContentHeight(height + 1)
    }
  }, [api, setContentHeight])

  return (
    <OverviewModuleCarouselContext.Provider value={{ contentHeight }}>
      <Carousel setApi={setApi}>
        <SpaceBetween className="relative mb-10 items-end">
          <FlexColumn>
            {moduleIndex !== undefined ? (
              <PreviewActionBar className="mb-1.5" moduleIndex={moduleIndex} />
            ) : null}
            {title ? <TextHeading size="lg">{title}</TextHeading> : null}
            {description ? (
              <Flex
                className={classNames({
                  "xxl:mt-1.5 md:mt-0.5": Boolean(title),
                })}
              >
                <TextBody
                  className="mt-1 max-w-[620px] text-text-secondary"
                  size="md"
                >
                  {description}
                </TextBody>
              </Flex>
            ) : null}
          </FlexColumn>
          {canScroll ? (
            <Media greaterThanOrEqual="lg">
              <CarouselNavigationArrows variant="static">
                <CarouselNavigationArrowsPrevious />
                <CarouselNavigationArrowsNext />
              </CarouselNavigationArrows>
            </Media>
          ) : null}
        </SpaceBetween>
        <CarouselContent
          allowOverflow
          className="h-full items-stretch gap-4"
          ref={containerRef}
        >
          {children}
        </CarouselContent>
      </Carousel>
    </OverviewModuleCarouselContext.Provider>
  )
}
